import React, { useContext, useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import AppContext from "../../context/AppContext";
import translations from "./Translations";
import {
  BLANK_FILTER_LABEL,
  BLANK_FILTER_VALUE,
  getColumnValues,
} from "../../utils/taskListFilters";

const FILTER_OPTION_ROW_HEIGHT_PX = 28;
const FILTER_OPTION_VIEWPORT_HEIGHT_PX = 192;
const FILTER_OPTION_OVERSCAN_ROWS = 5;

/*
   Fallback panel for columns with no distinct-value source. `contains` runs
   server-side exactly like `in` does, so these columns stay filterable.
*/
export const ColumnContainsFilter = ({ value, onApply, helpText }) => {
  const { langMode } = useContext(AppContext);
  const [draft, setDraft] = useState(value ?? "");

  useEffect(() => {
    setDraft(value ?? "");
  }, [value]);

  return (
    <div className="w-full">
      <div className="relative mb-2">
        <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onApply(draft);
          }}
          placeholder={translations[langMode]?.inventoryFilterContainsPlaceholder ?? "Contains..."}
          className="w-full pl-6 pr-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#236DB4]/20 focus:border-[#236DB4]"
        />
      </div>
      <p className="text-[11px] leading-snug text-slate-400 px-0.5 pb-2">
        {helpText ?? translations[langMode]?.columnContainsHelp ?? "Case-insensitive match across every task in the workflow."}
      </p>
      <div className="flex items-center justify-between pt-2 border-t border-slate-100">
        <button
          type="button"
          onClick={() => {
            setDraft("");
            onApply("");
          }}
          className="text-[11px] font-medium text-slate-400 hover:underline"
        >
          {translations[langMode]?.clear ?? "Clear"}
        </button>
        <button
          type="button"
          onClick={() => onApply(draft)}
          className="px-2.5 py-1 text-[11px] font-semibold text-white bg-[#236DB4] rounded hover:bg-[#1d5a96]"
        >
          {translations[langMode]?.inventoryFilterOk ?? "OK"}
        </button>
      </div>
    </div>
  );
};

/*
   Distinct-value checkbox picker for a grid column's funnel, shared by the
   Task List and User Management grids.

   Options come from the server, never from the loaded page: with server-side
   paging the grid only holds `size` rows, so distinct values derived from it
   would be wrong.

   Value source, in order of precedence:
   - `staticOptions` ([{ value, label }]) -- answered locally;
   - `loadValues(query)` -- any async source resolving to string[]; used as
     returned, so a "" in the result is shown as "(Blanks)";
   - otherwise the task-list source, getColumnValues(workflowIds, ...), which
     drops blanks and always offers "(Blanks)" at the top.

   The list is virtualised because some columns have thousands of distinct
   values. This panel is presentational: it emits a value selection on OK and
   nothing else. All-checked and none-checked both mean "don't filter".
*/
const ColumnValueFilterList = ({
  workflowIds,
  column,
  selected,
  containsValue,
  onApply,
  onApplyContains,
  // Other columns' `in` selections ([{ column, value }]) -- the server narrows
  // this column's distinct values to rows that still match them.
  columnConditions = null,
  // Columns whose options are known client-side and whose stored value differs
  // from what the user should read. [{ value, label }].
  staticOptions = null,
  loadValues = null,
  // Optional display label for a stored value (e.g. a translated enum).
  formatLabel = null,
  containsHelpText,
}) => {
  const { langMode } = useContext(AppContext);
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  // True when the value source returned nothing for an unfiltered query, i.e.
  // this column has no distinct-value support yet.
  const [sourceUnavailable, setSourceUnavailable] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  // null until the option list lands, so an empty draft can't be mistaken for
  // "user unchecked everything" while the fetch is still in flight.
  const [draft, setDraft] = useState(null);
  const selectedKey = (selected || []).join("");
  // Callers rebuild the conditions array every render; key on its content so
  // the fetch effect does not refire for an identical payload.
  const columnConditionsKey = JSON.stringify(columnConditions || []);

  const staticOptionsKey = staticOptions
    ? staticOptions.map((option) => `${option?.value}:${option?.label}`).join("|")
    : "";

  const labelByValue = useMemo(() => {
    const map = new Map();
    (staticOptions || []).forEach((option) => {
      map.set(String(option?.value ?? ""), String(option?.label ?? option?.value ?? ""));
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staticOptionsKey]);

  // The dropdown's own search box filters the OPTION LIST, not the rows, so it
  // re-queries the value source rather than touching the grid.
  useEffect(() => {
    let cancelled = false;
    setOptionsLoading(true);

    // Client-side option list: match on the visible label, keep the stored
    // value, and never offer "(Blanks)".
    if (staticOptions) {
      const search = String(query ?? "").trim().toLowerCase();
      const values = staticOptions
        .filter((option) =>
          search ? String(option?.label ?? "").toLowerCase().includes(search) : true
        )
        .map((option) => String(option?.value ?? ""))
        .filter(Boolean);
      setSourceUnavailable(false);
      setOptions(values);
      const preselected = selected || [];
      setDraft(new Set(preselected.length ? preselected : values));
      setOptionsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const request = loadValues
      ? Promise.resolve(loadValues(query)).then((values) => {
          const list = (values || []).map((value) => String(value ?? ""));
          // Keep the server's blank entry, but pin it to the top like the task grid does.
          return list.includes(BLANK_FILTER_VALUE)
            ? [BLANK_FILTER_VALUE, ...list.filter((value) => value !== BLANK_FILTER_VALUE)]
            : list;
        })
      : getColumnValues(workflowIds, column, query, columnConditions).then((values) =>
          values.length
            ? [BLANK_FILTER_VALUE, ...values.filter((value) => value !== BLANK_FILTER_VALUE)]
            : []
        );

    request
      .then((values) => {
        if (cancelled) return;
        // Only treat an empty result as "unsupported" when nothing was typed.
        // An empty result for a search term just means nothing matched.
        setSourceUnavailable(values.length === 0 && !String(query ?? "").trim());
        setOptions(values);
        const preselected = selected || [];
        setDraft(new Set(preselected.length ? preselected : values));
        setOptionsLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error(`Failed to load filter values for "${column}":`, error);
        setOptions([]);
        setSourceUnavailable(true);
        setDraft(new Set());
        setOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowIds, column, query, selectedKey, staticOptionsKey, columnConditionsKey]);

  // No distinct values for this column. `contains` still runs server-side, so
  // the column stays filterable rather than opening an empty, useless funnel.
  if (!optionsLoading && sourceUnavailable) {
    return <ColumnContainsFilter value={containsValue} onApply={onApplyContains} helpText={containsHelpText} />;
  }

  const toggleValue = (value) => {
    setDraft((prev) => {
      const next = new Set(prev || []);
      next.has(value) ? next.delete(value) : next.add(value);
      return next;
    });
  };
  const selectAll = () => setDraft(new Set(options));
  const clearAll = () => setDraft(new Set());

  const apply = () => {
    const picked = draft ? [...draft] : [];
    // All-checked and none-checked both mean "don't filter this column".
    // Sending an empty selection as a real predicate would match nothing.
    if (picked.length === 0 || picked.length === options.length) onApply([]);
    else onApply(picked);
  };

  const labelFor = (value) => {
    if (value === BLANK_FILTER_VALUE) return BLANK_FILTER_LABEL;
    const fromStatic = labelByValue.get(String(value));
    if (fromStatic !== undefined) return fromStatic;
    return formatLabel ? formatLabel(value) : value;
  };

  const startIndex = Math.max(
    0,
    Math.floor(scrollTop / FILTER_OPTION_ROW_HEIGHT_PX) - FILTER_OPTION_OVERSCAN_ROWS
  );
  const endIndex = Math.min(
    options.length,
    Math.ceil((scrollTop + FILTER_OPTION_VIEWPORT_HEIGHT_PX) / FILTER_OPTION_ROW_HEIGHT_PX) +
      FILTER_OPTION_OVERSCAN_ROWS
  );
  const windowedOptions = options.slice(startIndex, endIndex);

  return (
    <div className="w-full">
      <div className="relative mb-2">
        <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={translations[langMode]?.inventoryFilterSearchValues ?? "Search values..."}
          className="w-full pl-6 pr-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#236DB4]/20 focus:border-[#236DB4]"
        />
      </div>
      <div
        className="overflow-y-auto -mx-1 px-1"
        style={{ height: FILTER_OPTION_VIEWPORT_HEIGHT_PX }}
        onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
      >
        {optionsLoading && (
          <p className="text-xs text-slate-400 px-2 py-3 text-center">{translations[langMode]?.inventoryFilterLoadingValues ?? "Loading values..."}</p>
        )}
        {!optionsLoading && options.length === 0 && (
          <p className="text-xs text-slate-400 px-2 py-3 text-center">{translations[langMode]?.inventoryFilterNoValues ?? "No values"}</p>
        )}
        {!optionsLoading && options.length > 0 && (
          <div style={{ height: options.length * FILTER_OPTION_ROW_HEIGHT_PX, position: "relative" }}>
            {windowedOptions.map((value, index) => {
              const rowIndex = startIndex + index;
              return (
                <label
                  key={`${value}-${rowIndex}`}
                  className="absolute inset-x-0 flex items-center gap-2 px-2 hover:bg-slate-50 rounded cursor-pointer text-xs"
                  style={{
                    top: rowIndex * FILTER_OPTION_ROW_HEIGHT_PX,
                    height: FILTER_OPTION_ROW_HEIGHT_PX,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(draft?.has(value))}
                    onChange={() => toggleValue(value)}
                    className="accent-[#236DB4] w-3.5 h-3.5 shrink-0"
                  />
                  <span className={`truncate ${value === BLANK_FILTER_VALUE ? "italic text-slate-400" : ""}`}>
                    {labelFor(value)}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </div>
      <div className="flex items-center justify-between pt-2 mt-2 border-t border-slate-100">
        <div className="flex gap-2">
          <button type="button" onClick={selectAll} className="text-[11px] font-medium text-[#236DB4] hover:underline">{translations[langMode]?.userFilterAll ?? "All"}</button>
          <button type="button" onClick={clearAll} className="text-[11px] font-medium text-slate-400 hover:underline">{translations[langMode]?.userFilterNone ?? "None"}</button>
        </div>
        <button type="button" onClick={apply} className="px-2.5 py-1 text-[11px] font-semibold text-white bg-[#236DB4] rounded hover:bg-[#1d5a96]">{translations[langMode]?.inventoryFilterOk ?? "OK"}</button>
      </div>
    </div>
  );
};

export default ColumnValueFilterList;
